"use client";

import { Page, PageHeader } from "@commerce/components/page";
import { useWorkspace } from "@commerce/components/use-workspace";
import { Link, useLocation } from "@tanstack/react-router";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import { InfoTip } from "@ui/components/info-tip";
import { cn } from "@ui/lib";
import type { LucideIcon } from "lucide-react";
import {
	BellIcon,
	DatabaseIcon,
	GaugeIcon,
	GlobeIcon,
	KeyRoundIcon,
	LockKeyholeIcon,
	ScrollTextIcon,
	ShieldCheckIcon,
	ShieldUserIcon,
	SlidersHorizontalIcon,
	UserRoundIcon,
	UsersRoundIcon,
} from "lucide-react";
import type { ReactNode } from "react";

interface SettingsNavItem {
	label: string;
	href: string;
	icon: LucideIcon;
}

interface SettingsNavGroup {
	label: string;
	items: SettingsNavItem[];
}

/**
 * The one Settings area. Retailer settings (/app/$slug/settings/*) and your
 * own account (/app/settings/*) live on different routes but share this
 * layout and navigation, so they read as one place.
 */
export function SettingsLayout({
	isOrganizationAdmin,
	children,
}: {
	/** Owners and admins also see usage, API keys, audit log and roles. */
	isOrganizationAdmin: boolean;
	children: ReactNode;
}) {
	const pathname = useLocation({ select: (location) => location.pathname });
	const { organization } = useWorkspace();

	const groups: SettingsNavGroup[] = [];
	if (organization) {
		const base = `/app/${organization.slug}/settings`;
		groups.push({
			label: organization.name,
			items: [
				{
					label: "General",
					href: `${base}/general`,
					icon: SlidersHorizontalIcon,
				},
				{
					label: "Team",
					href: `${base}/members`,
					icon: UsersRoundIcon,
				},
				...(isOrganizationAdmin
					? [
							{
								label: "Usage",
								href: `${base}/usage`,
								icon: GaugeIcon,
							},
							{
								label: "API keys",
								href: `${base}/api-keys`,
								icon: KeyRoundIcon,
							},
							{
								label: "Domains",
								href: `${base}/domains`,
								icon: GlobeIcon,
							},
							{
								label: "Shopper data",
								href: `${base}/privacy-data`,
								icon: ShieldUserIcon,
							},
							{
								label: "Audit log",
								href: `${base}/audit`,
								icon: ScrollTextIcon,
							},
							{
								label: "Roles",
								href: `${base}/roles`,
								icon: ShieldCheckIcon,
							},
						]
					: []),
			],
		});
	}
	groups.push({
		label: "Your account",
		items: [
			{
				label: "Profile",
				href: "/app/settings/general",
				icon: UserRoundIcon,
			},
			{
				label: "Security",
				href: "/app/settings/security",
				icon: LockKeyholeIcon,
			},
			{
				label: "Notifications",
				href: "/app/settings/notifications",
				icon: BellIcon,
			},
			{
				label: "Privacy and data",
				href: "/app/settings/privacy",
				icon: DatabaseIcon,
			},
		],
	});

	const isActive = (href: string) =>
		pathname === href || pathname.startsWith(`${href}/`);

	return (
		<Page>
			<PageHeader title="Settings" />
			<div className="flex flex-col gap-6 lg:grid lg:grid-cols-[12.5rem_minmax(0,1fr)] lg:gap-12">
				<nav aria-label="Settings">
					{/* Phones and tablets: one scrollable row of pills. */}
					<ul className="no-scrollbar -mx-4 flex gap-1 overflow-x-auto px-4 md:-mx-8 md:px-8 lg:hidden">
						{groups.map((group, index) => (
							<li
								key={group.label}
								className="flex shrink-0 gap-1"
							>
								{index > 0 ? (
									<span
										aria-hidden
										className="mx-1 my-1.5 w-px bg-border"
									/>
								) : null}
								{group.items.map((item) => (
									<Link
										key={item.href}
										to={item.href}
										aria-current={
											isActive(item.href)
												? "page"
												: undefined
										}
										className={cn(
											"flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-sm outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/40",
											isActive(item.href)
												? "bg-foreground text-background"
												: "bg-muted/70 text-muted-foreground hover:text-foreground",
										)}
									>
										{item.label}
									</Link>
								))}
							</li>
						))}
					</ul>

					{/* Desktop: a quiet list beside the content. */}
					<div className="sticky top-8 hidden flex-col gap-6 lg:flex">
						{groups.map((group) => (
							<div
								key={group.label}
								className="flex flex-col gap-1"
							>
								<p className="truncate px-3 pb-1 font-medium text-muted-foreground text-xs">
									{group.label}
								</p>
								{group.items.map((item) => (
									<Link
										key={item.href}
										to={item.href}
										aria-current={
											isActive(item.href)
												? "page"
												: undefined
										}
										className={cn(
											"flex h-8 items-center gap-2.5 rounded-xl px-3 text-sm outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/40",
											isActive(item.href)
												? "bg-muted font-medium text-foreground [&>svg]:text-brand"
												: "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
										)}
									>
										<item.icon
											className="size-4 shrink-0"
											aria-hidden
										/>
										<span className="truncate">
											{item.label}
										</span>
									</Link>
								))}
							</div>
						))}
					</div>
				</nav>
				<div className="flex min-w-0 max-w-3xl flex-col gap-6">
					{children}
				</div>
			</div>
		</Page>
	);
}

/** Title block at the top of one settings page. */
export function SettingsPageIntro({
	title,
	description,
	info,
	actions,
}: {
	title: string;
	/** One short line, only when the title can't speak for itself. */
	description?: ReactNode;
	/** Explanation behind an ⓘ next to the title. */
	info?: ReactNode;
	actions?: ReactNode;
}) {
	return (
		<div className="flex flex-wrap items-end justify-between gap-3">
			<div className="flex min-w-0 flex-col gap-1">
				<h2 className="flex items-center gap-1.5 font-semibold text-lg tracking-tight">
					{title}
					{info ? (
						<InfoTip label={`About ${title}`}>{info}</InfoTip>
					) : null}
				</h2>
				{description ? (
					<p className="max-w-2xl text-pretty text-muted-foreground text-sm">
						{description}
					</p>
				) : null}
			</div>
			{actions ? (
				<div className="flex items-center gap-2">{actions}</div>
			) : null}
		</div>
	);
}

/** Shown instead of an admin-only page to members who open its link. */
export function AdminOnlyNotice({ what }: { what: string }) {
	return (
		<Empty className="rounded-3xl border">
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<LockKeyholeIcon />
				</EmptyMedia>
				<EmptyTitle>Only owners and admins can see {what}</EmptyTitle>
				<EmptyDescription>Ask one of them for access.</EmptyDescription>
			</EmptyHeader>
		</Empty>
	);
}
